import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Clock, Upload, CreditCard, QrCode, Copy, FileText, Download, ExternalLink, Hash } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { useVendorSubscription, useVendorInvoices, useVendorRefresh } from "@/hooks/useVendor";
import { formatPrice, formatDate } from "@/lib/format";
import { TrialCountdown } from "@/pages/vendor/VendorLayout";
import { Pill } from "@/pages/admin/ui";
import PhoneField, { isValidPhoneNumber } from "@/components/PhoneField";

const STATUS_BADGE = {
  active: { label: "🟢 Actif", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  trial: { label: "🎁 Essai gratuit", cls: "bg-brand-light text-brand border-brand/20" },
  expired: { label: "⚠️ Abonnement requis", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  blocked: { label: "🔒 Suspendu", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};
const TYPE_LABEL = { link: "Paiement par lien", manual: "Paiement manuel", qr: "Paiement par QR Code", other: "Autre" };
const mediaSrc = (u) => (!u ? "" : u.startsWith("http") || u.startsWith("/api/") ? fileUrl(u) : u);

export default function VendorSubscription() {
  const { data, isLoading } = useVendorSubscription();
  const { data: invoices } = useVendorInvoices();
  const refresh = useVendorRefresh();
  const [step, setStep] = useState(null); // { plan, request }
  const [methods, setMethods] = useState([]);
  const [selected, setSelected] = useState("");
  const [txid, setTxid] = useState("");
  const [phone, setPhone] = useState("");
  const [proof, setProof] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  if (isLoading || !data) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>;
  const status = data.status;
  const badge = STATUS_BADGE[status.status] || STATUS_BADGE.trial;
  const selectedMethod = methods.find((m) => m.id === selected);

  // Étape 1 : choisir l'offre → création de la demande + chargement des moyens de paiement admin
  const choose = async (p) => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/vendor/subscription/checkout", { plan_id: p.id });
      const { data: pm } = await api.get("/vendor/subscription/payment-methods");
      setMethods(pm.methods || []);
      setSelected(""); setTxid(""); setPhone(""); setProof(null);
      setStep({ plan: p, request: res.request });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data: res } = await api.post("/vendor/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProof(res.url);
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const pay = async () => {
    if (!selectedMethod) return toast.error("Choisissez un moyen de paiement");
    if (phone && !isValidPhoneNumber(phone)) return toast.error("Numéro de téléphone invalide. Vérifiez l'indicatif et le nombre de chiffres.");
    setBusy(true);
    try {
      await api.post("/vendor/subscription/pay", {
        method_id: selectedMethod.id, phone, transaction_id: txid.trim(), proof_path: proof || "",
      });
      toast.success("Paiement soumis ! L'administrateur va le vérifier et activer votre abonnement.");
      setStep(null);
      refresh(["vendor-subscription", "vendor-me", "vendor-invoices"]);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const copyText = async (t) => { try { await navigator.clipboard.writeText(t); toast.success("Copié !"); } catch { toast.error("Copie impossible"); } };

  return (
    <div className="space-y-6" data-testid="vendor-subscription">
      <h1 className="font-display text-2xl font-extrabold text-ink">Mon abonnement</h1>

      <div className="rounded-2xl border border-line bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Statut actuel</p>
            <p className="mt-1 font-display text-xl font-extrabold text-ink">{status.plan_name || "Période d'essai"}</p>
            {status.status === "active" && status.subscription_expires_at && (
              <p className="mt-1 text-sm text-slate-500">Expire le {formatDate(status.subscription_expires_at)}</p>
            )}
          </div>
          <div className="text-right">
            <span className={`inline-block rounded-full border px-3 py-1 text-xs font-bold ${badge.cls}`}>{badge.label}</span>
            {status.status === "trial" && <div className="mt-3 rounded-xl bg-brand p-2"><TrialCountdown targetIso={status.trial_ends_at} /></div>}
          </div>
        </div>
        <p className="mt-4 text-xs text-slate-500">Commission plateforme appliquée sur vos ventes : <span className="font-bold text-ink">{data.commission_percent}%</span></p>
      </div>

      {/* -------- Paiement de l'abonnement (même système que les commandes) -------- */}
      {step && (
        <div className="rounded-2xl border-2 border-brand/30 bg-white p-5" data-testid="subscription-payment">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-display text-lg font-extrabold text-ink">Paiement — {step.plan.name}</p>
              <p className="text-xs text-slate-500">Montant exact à payer : <span className="font-black text-brand">{formatPrice(step.plan.price)}</span> · Durée {step.plan.duration_days} jours</p>
            </div>
            <button onClick={() => setStep(null)} className="text-xs font-bold text-slate-400 hover:text-rose-500" data-testid="sub-cancel">Annuler</button>
          </div>

          {methods.length === 0 ? (
            <p className="mt-4 rounded-xl border border-dashed border-line p-6 text-center text-sm text-slate-500">Aucun moyen de paiement configuré. Contactez l'administrateur.</p>
          ) : (
            <>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {methods.map((m, i) => (
                  <button key={m.id} onClick={() => setSelected(m.id)} data-testid={`sub-method-${i}`}
                    className={`relative flex items-center gap-3 rounded-2xl border-2 bg-white p-4 text-left transition-all ${selected === m.id ? "border-brand shadow-lg shadow-brand/10" : "border-line hover:border-brand/40"}`}>
                    {m.logo_url ? (
                      <img src={mediaSrc(m.logo_url)} alt={m.name} className="h-11 w-11 shrink-0 rounded-xl object-contain" />
                    ) : (
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-light text-brand">
                        {m.payment_type === "qr" ? <QrCode className="h-5 w-5" /> : <CreditCard className="h-5 w-5" />}
                      </span>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-extrabold text-ink">{m.name}</span>
                      <span className="block text-[11px] font-semibold text-slate-500">{TYPE_LABEL[m.payment_type] || ""}</span>
                    </span>
                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${selected === m.id ? "border-brand bg-brand text-white" : "border-slate-300"}`}>
                      {selected === m.id && <CheckCircle2 className="h-3 w-3" />}
                    </span>
                  </button>
                ))}
              </div>

              {selectedMethod && (
                <div className="mt-4 space-y-4 rounded-2xl border border-line bg-surface p-4" data-testid="sub-method-detail">
                  {selectedMethod.payment_type === "manual" && selectedMethod.payment_number && (
                    <div className="flex items-stretch gap-2">
                      <div className="flex min-w-0 flex-1 items-center rounded-xl border border-line bg-white px-3.5 py-3 font-mono text-sm font-bold text-ink" data-testid="sub-number">{selectedMethod.payment_number}</div>
                      <button onClick={() => copyText(selectedMethod.payment_number)} className="flex shrink-0 items-center gap-1.5 rounded-xl bg-brand px-4 text-xs font-bold text-white"><Copy className="h-4 w-4" /> Copier</button>
                    </div>
                  )}
                  {selectedMethod.beneficiary_name && <p className="text-sm"><span className="text-slate-500">Bénéficiaire : </span><span className="font-bold text-ink">{selectedMethod.beneficiary_name}</span></p>}
                  {selectedMethod.payment_type === "link" && selectedMethod.link && (
                    <button onClick={() => window.open(selectedMethod.link, "_blank", "noopener")} data-testid="sub-pay-link"
                      className="flex items-center gap-2 rounded-xl bg-brand px-6 py-3 text-sm font-bold text-white">
                      Payer maintenant <ExternalLink className="h-4 w-4" />
                    </button>
                  )}
                  {selectedMethod.payment_type === "qr" && (
                    <>
                      {selectedMethod.qr_image_url && (
                        <div className="flex justify-center"><img src={mediaSrc(selectedMethod.qr_image_url)} alt="QR Code" className="h-52 w-52 rounded-2xl border-2 border-line bg-white object-contain p-3" data-testid="sub-qr" /></div>
                      )}
                      {selectedMethod.address && (
                        <div className="flex items-stretch gap-2">
                          <div className="flex min-w-0 flex-1 items-center rounded-xl border border-line bg-white px-3.5 py-3 font-mono text-xs font-bold text-ink" data-testid="sub-address"><span className="truncate">{selectedMethod.address}</span></div>
                          <button onClick={() => copyText(selectedMethod.address)} className="flex shrink-0 items-center gap-1.5 rounded-xl bg-brand px-4 text-xs font-bold text-white"><Copy className="h-4 w-4" /></button>
                        </div>
                      )}
                      {selectedMethod.network && <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm font-bold text-amber-800" data-testid="sub-network">Réseau : {selectedMethod.network}</p>}
                    </>
                  )}
                  {selectedMethod.instructions && (
                    <ol className="space-y-1.5 rounded-xl border border-brand/15 bg-white p-4" data-testid="sub-instructions">
                      {selectedMethod.instructions.split("\n").map((l) => l.trim()).filter(Boolean).map((l, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand text-[9px] font-black text-white">{i + 1}</span>
                          <span>{l.replace(/^\d+[.)]\s*/, "")}</span>
                        </li>
                      ))}
                    </ol>
                  )}

                  <div className="space-y-3 border-t border-line pt-4">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <PhoneField testId="sub" label="Numéro utilisé pour payer" value={phone} onChange={setPhone} />
                      <label className="block">
                        <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
                          {selectedMethod.payment_type === "qr" ? "TXID / Hash" : "Référence de transaction"} {selectedMethod.require_txid && <span className="text-rose-500">*</span>}
                        </span>
                        <div className="relative">
                          <Hash className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                          <input value={txid} onChange={(e) => setTxid(e.target.value)} data-testid="sub-txid" className="h-11 w-full rounded-lg border border-line bg-white pl-9 pr-3 text-sm" />
                        </div>
                      </label>
                    </div>
                    <div>
                      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Preuve de paiement {selectedMethod.require_proof ? <span className="text-rose-500">*</span> : "— facultatif"}</span>
                      <input ref={fileRef} type="file" accept="image/*,application/pdf" hidden onChange={upload} />
                      <button type="button" onClick={() => fileRef.current?.click()} data-testid="sub-proof"
                        className="flex items-center gap-2 rounded-lg border border-dashed border-line bg-white px-4 py-2.5 text-sm font-bold text-ink hover:border-brand">
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : proof ? <FileText className="h-4 w-4 text-emerald-600" /> : <Upload className="h-4 w-4" />}
                        {proof ? "Preuve ajoutée ✓" : "Téléverser une preuve"}
                      </button>
                    </div>
                    <button onClick={pay} disabled={busy} data-testid="sub-pay-submit"
                      className="btn-glow flex w-full items-center justify-center gap-2 rounded-full bg-brand py-3.5 text-sm font-bold text-white disabled:opacity-60">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      ✅ J'ai effectué le paiement
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div>
        <h2 className="mb-3 font-display text-lg font-bold text-ink">Choisir un abonnement</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.plans.map((p) => (
            <div key={p.id} data-testid={`plan-${p.name}`} className={`relative rounded-2xl border-2 bg-white p-5 ${p.badge ? "border-brand" : "border-line"}`}>
              {p.badge && <span className="absolute -top-2.5 left-4 rounded-full bg-brand px-2.5 py-0.5 text-[10px] font-black uppercase text-white">{p.badge}</span>}
              <p className="font-display text-lg font-extrabold text-ink">{p.name}</p>
              {p.description && <p className="mt-0.5 text-xs text-slate-500">{p.description}</p>}
              <p className="mt-1 font-display text-2xl font-black text-brand">{formatPrice(p.price)}<span className="text-sm font-bold text-slate-400"> / {p.duration_days}j</span></p>
              <ul className="mt-4 space-y-1.5 text-sm text-slate-600">
                {(p.features || []).map((f) => <li key={f} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" /> {f}</li>)}
              </ul>
              <button onClick={() => choose(p)} disabled={busy} data-testid={`choose-plan-${p.name}`} className="mt-5 w-full rounded-full bg-brand py-2.5 text-sm font-bold text-white hover:bg-brand-dark disabled:opacity-60">
                Choisir {p.name}
              </button>
            </div>
          ))}
        </div>
      </div>

      {data.requests?.length > 0 && (
        <div className="rounded-2xl border border-line bg-white p-5">
          <p className="font-display text-base font-bold text-ink">Mes demandes d'abonnement</p>
          <div className="mt-3 divide-y divide-line">
            {data.requests.filter((r) => r.status !== "awaiting_payment").map((r) => (
              <div key={r.id} className="flex items-center justify-between py-2.5 text-sm">
                <div>
                  <span className="font-bold text-ink">{r.plan_name}</span> · {formatPrice(r.price)}
                  <br />
                  <span className="text-xs text-slate-400">
                    {formatDate(r.created_at)}{r.payment_method ? ` · ${r.payment_method}` : ""}{r.transaction_id ? ` · Réf ${r.transaction_id}` : ""}
                  </span>
                </div>
                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${r.status === "confirmed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : r.status === "rejected" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                  {r.status === "confirmed" ? "✅ Confirmé" : r.status === "rejected" ? "Refusé" : "🟠 En vérification"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Factures d'abonnement */}
      <div className="rounded-2xl border border-line bg-white p-5" data-testid="vendor-invoices">
        <p className="font-display text-base font-bold text-ink">Mes factures</p>
        {!invoices?.length ? (
          <p className="mt-3 text-sm text-slate-400">Aucune facture pour le moment. Elle apparaîtra ici après validation de votre abonnement.</p>
        ) : (
          <div className="mt-3 divide-y divide-line">
            {invoices.map((inv) => (
              <div key={inv.id} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm" data-testid={`invoice-${inv.id}`}>
                <div>
                  <p className="font-bold text-ink">{inv.invoice_number} · {inv.plan_name}</p>
                  <p className="text-xs text-slate-400">
                    {formatDate(inv.created_at)} · {formatPrice(inv.price)} · Expire le {formatDate(inv.expires_at)}
                    {inv.payment_method ? ` · ${inv.payment_method}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Pill tone="green">Payée</Pill>
                  <a href={`${api.defaults.baseURL}/vendor/invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer"
                    data-testid={`invoice-pdf-${inv.id}`}
                    className="flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-xs font-bold text-white hover:bg-brand">
                    <Download className="h-3.5 w-3.5" /> PDF
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {status.status === "trial" && (
        <p className="flex items-center gap-2 text-xs text-slate-400"><Clock className="h-3.5 w-3.5" /> Profitez de votre essai gratuit pour tester toutes les fonctionnalités avant de choisir une offre.</p>
      )}
    </div>
  );
}
